import classNames from 'classnames';
import { useEffect, useState, type FC } from 'react';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom';

import styles from './sidebar-primary-navigation.module.scss';
import { type PrimaryNavigationItem, type SidebarPrimaryNavigationProps } from './types';
import { Button, Flex } from 'antd';
import { Routes, buildOrganizationRoute } from '@shared/constants/routes';
import { hasRequiredPermission } from '@shared/lib/permissions';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import {
  useGetMeUserMeGetQuery,
  // useLazyGetAgentInfoAgentInfoGetQuery,
} from '@shared/api';
import { authActions, authSelectors } from '@shared/redux/ducks/auth';
import { Header } from './ui/header';
import { Text } from '@shared/ui/antd';
import { PlusBlueWithoutBackground } from '@shared/assets/icons/components';
import { useGetAgentsLazy } from '@shared/hooks/use-get-agents-lazy';
import { DropdownList } from '@shared/ui/dropdown-list/dropdown-list';
import { useToggle } from '@shared/hooks/use-toggle';
import { CreateAiAgentModal } from './ui/create-ai-agent-modal/createa-ai-agent-modal';
import { router } from '@app/providers/router/router-provider';
// import { useCreateAgent } from '@shared/hooks/use-create-ai-agent';
import { NavigationItem } from './ui/navigation-item/navigation-item';
import { NavigationSection } from './ui/navigation-section/navigation-section';
import { useTranslation } from 'react-i18next';

type AgentModalMode = 'create' | 'edit';

export const SidebarPrimaryNavigation: FC<SidebarPrimaryNavigationProps> = ({
  primaryNavigation,
}) => {
  const { t } = useTranslation('translation');
  const isAuthorized = useAppSelector(authSelectors.selectIsAuthorized);
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const selfPermission = useAppSelector(authSelectors.selectSelfPermission);
  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedRouteByDropdown, setSelectedRouteByDropdown] = useState<string>();

  const [agentModalMode, setAgentModalMode] = useState<AgentModalMode>('create');
  const [activeAgentId, setActiveAgentId] = useState<number | null>(null);

  const { fetchAgents, agents, isLoading } = useGetAgentsLazy();
  // const [getAgentInfo] = useLazyGetAgentInfoAgentInfoGetQuery();

  const {
    state: isAiAgentCreateModalOpen,
    setFalse: closeAiAgentCreateModal,
    setTrue: openAiAgentCreateModal,
  } = useToggle();

  const openCreate = () => {
    setAgentModalMode('create');
    setActiveAgentId(null);
    openAiAgentCreateModal();
  };

  // const openEdit = (agentId: number) => {
  //   setAgentModalMode('edit');
  //   setActiveAgentId(agentId);
  //   openAiAgentCreateModal();
  // };

  const visibleItems = primaryNavigation.filter(
    (nav) => nav.isVisible !== false && hasRequiredPermission(selfPermission, nav.permission),
  );

  const secondaryRoutes = [
    Routes.team.root,
    Routes.billing.root,
    Routes.profile.user.root,
    Routes.support.root,
    Routes.authentication.logOut,
  ];

  const mainItems = visibleItems.filter(
    (nav) => !secondaryRoutes.includes(nav.rootRoute) && !nav.isForceHiddenMainNavigation,
  );
  const secondaryItems = visibleItems.filter(
    (nav) => secondaryRoutes.includes(nav.rootRoute) && !nav.isForceHiddenMainNavigation,
  );

  const { data } = useGetMeUserMeGetQuery(undefined, {
    skip: !isAuthorized,
  });

  const handlePrimaryNavigate = (nav: PrimaryNavigationItem) => {
    return navigation(nav.route);
  };

  const handleSelectRouteByDropdown = (route: string) => {
    setSelectedRouteByDropdown((prev) => {
      const next = prev === route ? undefined : route;
      if (!prev && currentOrganization?.id) {
        fetchAgents({ organizationId: currentOrganization.id });
      }
      return next;
    });
  };

  useEffect(() => {
    if (!currentOrganization?.id) return;
    if (selectedRouteByDropdown !== Routes.aiAgents.root) return;

    fetchAgents({ organizationId: currentOrganization.id });
  }, [currentOrganization?.id, selectedRouteByDropdown, fetchAgents]);

  // const handleDuplicate = async (agentId: number) => {
  //   const agentInfo = await getAgentInfo({ agentId }).unwrap();

  //   const createValues = {
  //     ...agentInfo,
  //     name: `${agentInfo.name} (копия)`,
  //   };

  //   await createAgent(createValues);

  //   fetchAgents({ organizationId: currentOrganization.id });
  // };

  const handleLogout = () => {
    dispatch(authActions.logout());
  };

  const allowedRoutes = [
    Routes.analytics.root,
    Routes.dialogs.root,
    Routes.channels.root,
    Routes.presets.root,
    Routes.aiAgents.root,
    Routes.knowledgeBase.root,
    Routes.preview.root,
    Routes.team.root,
    Routes.billing.root,
    Routes.profile.user.root,
  ];

  const withDropdownRoutes = [Routes.aiAgents.root];
  const isRouteAllowed = (route: string) => allowedRoutes.includes(route);
  const isOrganizationRequired = (route: string) =>
    allowedRoutes.includes(route) && route !== Routes.profile.user.root;
  const isCreateVisible = hasRequiredPermission(selfPermission, 'ADMIN');
  const isCreateDisabled = !currentOrganization?.id;

  return (
    <Flex className={styles.sidebarPrimaryNav}>
      <Flex className={styles.sidebarPrimaryNav__container} align="start" vertical>
        <div className={styles.sidebarPrimaryNav__header}>
          <Header userInfo={data} />
        </div>

        {isCreateVisible && (
          <Button
            className={styles.sidebarPrimaryNav__createButton}
            icon={<PlusBlueWithoutBackground color="#234EEE" />}
            iconPosition="start"
            onClick={openCreate}
            disabled={isCreateDisabled}
            variant="text"
          >
            {t('sidebar.navigation.createAiAgents')}
          </Button>
        )}

        <Flex justify="space-between" className={styles.sidebarPrimaryNav__content} vertical>
          <NavigationSection>
            {mainItems.map((nav) => {
              const isRouteBlocked = !isRouteAllowed(nav.rootRoute);
              const isButtonDisabled =
                isRouteBlocked ||
                (isOrganizationRequired(nav.rootRoute) && !currentOrganization?.id);
              const hasDropdown = withDropdownRoutes.includes(nav.rootRoute);
              const isDropdownOpen = selectedRouteByDropdown === nav.rootRoute;
              const isActive = location?.pathname.includes(nav.rootRoute);

              return (
                <Flex
                  key={nav.rootRoute}
                  vertical
                  className={styles.sidebarPrimaryNav__itemWrapper}
                >
                  <NavigationItem
                    item={nav}
                    isActive={isActive}
                    isDisabled={isButtonDisabled}
                    hasDropdown={hasDropdown}
                    isDropdownOpen={isDropdownOpen}
                    onClick={() => handlePrimaryNavigate(nav)}
                    onDropdownToggle={() => handleSelectRouteByDropdown(nav.rootRoute)}
                  />

                  {hasDropdown && isDropdownOpen && (
                    <div className={styles.sidebarPrimaryNav__dropdown}>
                      <DropdownList
                        isOpen
                        items={agents}
                        isLoading={isLoading}
                        emptyPlaceholder={
                          <Text
                            variant="sm"
                            fontWeight={400}
                            className={styles.sidebarPrimaryNav__dropdownEmpty}
                          >
                            {t('sidebar.agentsEmpty')}
                          </Text>
                        }
                        renderItem={(agent) => {
                          const agentRoute = currentOrganization?.id
                            ? buildOrganizationRoute(
                                Routes.organizationScope.aiAgent,
                                currentOrganization.id,
                                {
                                  agentId: agent.id,
                                },
                              )
                            : generatePath(Routes.aiAgents.agent, {
                                agentId: agent.id,
                              });
                          const isAgentActive =
                            matchPath(
                              { path: Routes.organizationScope.aiAgent, end: false },
                              location.pathname,
                            )?.params.agentId === String(agent.id);

                          return (
                            <div className={styles.sidebarPrimaryNav__dropdownItem}>
                              <Button
                                key={agent.id}
                                className={classNames(styles.sidebarPrimaryNav__dropdownButton, {
                                  [styles['sidebarPrimaryNav__dropdownButton--active']]:
                                    isAgentActive,
                                })}
                                variant="text"
                                onClick={() => router.navigate(agentRoute)}
                              >
                                <span className={styles.sidebarPrimaryNav__dropdownContent}>
                                  <Text
                                    className={styles.sidebarPrimaryNav__dropdownText}
                                    fontWeight={400}
                                    variant="sm"
                                    hasTooltip
                                    maxLength={15}
                                  >
                                    {agent.name}
                                  </Text>
                                </span>
                              </Button>
                            </div>
                          );
                        }}
                        maxHeight="200px"
                      />
                    </div>
                  )}
                </Flex>
              );
            })}
          </NavigationSection>

          <NavigationSection variant="secondary">
            {secondaryItems.map((nav) => {
              const isActive = location?.pathname.includes(nav.rootRoute);
              const isLogout = nav.rootRoute === Routes.authentication.logOut;
              const isButtonDisabled = !isLogout && !isRouteAllowed(nav.rootRoute);

              return (
                <NavigationItem
                  key={nav.rootRoute}
                  item={nav}
                  isActive={isActive}
                  isDisabled={isButtonDisabled}
                  hasDropdown={false}
                  isDropdownOpen={false}
                  onClick={() => (isLogout ? handleLogout() : handlePrimaryNavigate(nav))}
                />
              );
            })}
          </NavigationSection>

          <CreateAiAgentModal
            isOpen={isAiAgentCreateModalOpen}
            onClose={() => {
              closeAiAgentCreateModal();
              setActiveAgentId(null);
              setAgentModalMode('create');
            }}
            mode={agentModalMode}
            agentId={activeAgentId ?? undefined}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
